import * as React from "react";
import type { Subtract } from "utility-types";
import type { GlideFC } from "lib/glide-fc";
import type { GridCell, GridColumn } from "@glideapps/glide-data-grid";
import DataEditor, { GridCellKind } from "@glideapps/glide-data-grid";
import type { HeaderIcon } from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-sprites";
import { assert, assertNever, panic } from "@glideapps/ts-necessities";
import "twin.macro";
import { styled } from "webapp/styled-components";
import { Pager } from "webapp/ui/smart/card-collection/card-collection";
import type { WireDataGridListComponent } from "common/wire/base-components";

type Props = Subtract<WireDataGridListComponent, { kind: "list"; format: any }> & {
    onPageChange?: (newPage: number) => void;
};

function getHeaderIcon(kind: WireDataGridListComponent["columns"][0]["kind"]): HeaderIcon {
    switch (kind) {
        case "boolean":
            return "headerBoolean";
        case "image-uri":
            return "headerImage";
        case "string":
            return "headerString";
        default:
            assertNever(kind);
    }
}

const Wrapper = styled.div`
    > :first-child {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
    }
`;

export const DataGrid: GlideFC<Props> = p => {
    const { columns, items, paging, onPageChange } = p;

    assert(paging !== undefined);

    const { numPages, pageIndex, pageSize } = paging;

    const gridColumns = React.useMemo<readonly GridColumn[]>(() => {
        return columns.map(c => ({
            title: c.title,
            width: 200,
            icon: getHeaderIcon(c.kind),
        }));
    }, [columns]);

    const getCellContent = React.useCallback(
        (cell: readonly [number, number]): GridCell => {
            const [col, row] = cell;
            const data = items[row][col];

            if (columns[col].kind === "boolean") {
                return {
                    kind: GridCellKind.Boolean,
                    allowEdit: false,
                    allowOverlay: false,
                    data: data as boolean,
                    showUnchecked: true,
                };
            } else if (columns[col].kind === "image-uri") {
                return {
                    kind: GridCellKind.Image,
                    allowAdd: false,
                    allowOverlay: false,
                    data: [data as string],
                };
            } else if (columns[col].kind === "string") {
                return {
                    kind: GridCellKind.Text,
                    allowOverlay: false,
                    data: data as string,
                    displayData: data as string,
                };
            }

            panic("Should not happen");
        },
        [columns, items]
    );

    const style = React.useMemo(
        (): React.CSSProperties => ({
            height: 40 * (pageSize + 1) + 2,
        }),
        [pageSize]
    );

    return (
        <div tw="flex flex-col py-2">
            <Wrapper
                style={style}
                tw="relative w-full rounded-lg shadow-md border border-solid border-border-base overflow-hidden">
                <DataEditor
                    showTrailingBlankRow={false}
                    allowResize={false}
                    rowMarkers={false}
                    rowHeight={40}
                    headerHeight={40}
                    columns={gridColumns}
                    rows={items.length}
                    getCellContent={getCellContent}
                />
            </Wrapper>
            {numPages > 1 && (
                <div tw="self-center">
                    <Pager numPages={numPages} page={pageIndex.value} onPageChange={onPageChange} />
                </div>
            )}
        </div>
    );
};
